import { isDeepEqual } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'

/**
 * Compares the results of each of the provided expressions and returns true
 * only if none of the results are the same
 *
 * @param expressions The expressions to compare
 * @example
 * // Intrinsic values
 * { '!=': [ 'true', 'false' ] }
 * // ->
 * true
 * // Objects
 * { '!=': [ { value: 'true' }, { value: 'true' }, { value: 'false' } ] }
 * // ->
 * false
 * // Lists
 * { '!=': [ [ 1 ], [ 2 ], [ 3 ] ] }
 * // ->
 * true
 */
export interface NotEquals<out T = any> {
  /** {@link NotEquals} */
  readonly [Key.NotEquals]: T extends boolean
    ? readonly Expression<any>[]
    : never
}
export namespace NotEquals {
  export const isNotEquals: TypeChecker<NotEquals> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object<NotEquals>({
        [Key.NotEquals]: CommonArgs.isListOfExpressions,
      }),
  ).usingAlias('Fn.NotEquals')
  export const assertIsNotEquals: TypeAsserter<NotEquals> =
    TypeAsserter.Factory.from(isNotEquals)

  /**
   * @see {@link Node}
   */
  export class Node<T extends boolean = boolean> extends fnNode.Node<
    T,
    Key.NotEquals,
    {
      readonly fnNodes: readonly fnNode.Node[]
      /**
       * These have already been checked against each other, so you just need to
       * check that none of the {@link fnNodes} are equal to these nodes
       */
      readonly valueNodes: readonly expressionNode.ForValue[]
    }
  > {
    public readonly type = common.value.types.Intrinsic.Boolean

    constructor(nodes: (fnNode.Node | expressionNode.ForValue)[]) {
      const fnNodes: fnNode.Node[] = []
      const valueNodes: expressionNode.ForValue[] = []
      for (const node of nodes) {
        if (expressionNode.ForValue.isForValue(node)) {
          valueNodes.push(node)
        } else {
          fnNodes.push(node)
        }
      }
      super(Key.NotEquals, {
        fnNodes,
        valueNodes,
      })
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<NotEquals>['expressionsInArgs'] = fn =>
    fn[Key.NotEquals]
  export const HELPER: FnHelper<NotEquals> = {
    createNode: standardNodeCreator.usingExpressionList({
      // If there are no items, then it is reasonable to say all items are
      // unique
      emptyValue: true,
      finalizeArgs: (args: (Node | expressionNode.ForValue)[], stash) => [
        ...args,
        // Adds all the values that we set aside
        ...stash.values.map(value => new expressionNode.ForValue(value)),
      ],
      getArgExpressions: expressionsInArgs,
      handleValue: (node, stash) => {
        // NOTE: we need to nullish coalesce to `null` so that we treat
        // null & undefined as equal
        const currentValue = node.value ?? null
        // If this value is equal to some other value in the expression than the
        // whole expression is false
        if (stash.values.some(value => isDeepEqual(value, currentValue))) {
          return { return: false }
        }
        // Otherwise, we store the value for future comparisons and move on
        stash.values.push(currentValue)
        return undefined
      },
      nodeClass: Node,
      setupStash: (): { values: unknown[] } => ({ values: [] }),
      // NotEquals is the same with 0 or 1 arguments
      treatSingleArgAs: 'empty',
    }),
    expressionsInArgs,
    typeChecker: isNotEquals,
  }
}
